import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { ApiError } from "./api.ts";
import { ago, signalBars } from "./format.ts";

export function Logo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 3c4.6 6 9 10.8 9 15.6A9 9 0 0 1 7 18.6C7 13.8 11.4 9 16 3z" fill="var(--water)" />
      <path d="M11.5 18.5l3.2 3.2 6-6.4" fill="none" stroke="var(--leaf)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Wordmark() {
  return (
    <div className="wordmark">
      <b>think</b>
      <span>control</span>
    </div>
  );
}

const paths: Record<string, string> = {
  programs: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2",
  home: "M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z",
  admin: "M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5M17 11h5M19.5 8.5v5",
  alerts: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0",
  devices: "M4 6h16M4 12h16M4 18h10",
  accounts: "M3 21V7l9-4 9 4v14M9 21v-6h6v6",
  me: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0",
  back: "M15 18l-6-6 6-6",
  chev: "M9 18l6-6-6-6",
  refresh: "M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7",
  pin: "M9 4h6l-1 6 4 3v2H6v-2l4-3zM12 15v6",
  chart: "M4 20V10M10 20V4M16 20v-8M22 20H2",
  server: "M4 4h16v6H4zM4 14h16v6H4zM8 7h.01M8 17h.01",
  map: "M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14",
  antenna: "M12 12v9M8.5 8.5a5 5 0 0 1 7 0M5.6 5.6a9 9 0 0 1 12.8 0",
  usb: "M12 3v14M12 3l-2.5 3M12 3l2.5 3M12 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM12 13l-5-3V8M12 11l5-3V6M5.5 6.5h3v1.5h-3zM16 5h2v2h-2z",
  up: "M6 15l6-6 6 6",
  down: "M6 9l6 6 6-6",
  close: "M6 6l12 12M18 6L6 18",
  spark: "M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z",
};

export function Icon({ name, size = 20 }: { name: keyof typeof paths; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

export function Bars({ rssi }: { rssi: number | undefined }) {
  const n = signalBars(rssi);
  if (!n) return <span className="bars" aria-label="No signal reading" />;
  return (
    <span className={`bars ${n <= 2 ? "weak" : ""}`} title={`Signal ${rssi} dBm`} aria-label={`Signal ${n} of 4 bars`}>
      {[1, 2, 3, 4].map((i) => (
        <i key={i} className={i <= n ? "on" : ""} style={{ height: 4 + i * 3 }} />
      ))}
    </span>
  );
}

export const Spinner = () => <div className="spinner" role="status" aria-label="Loading" />;

export function Problem({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof ApiError || error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="banner crit" role="alert">
      {msg}{" "}
      {onRetry && (
        <button className="btn ghost" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** The current time, ticking so "3 min ago" stays true. */
export function useNow(everyMs = 30_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

/** Longest wait between tries while the server can't be reached. */
const BACKOFF_MAX_MS = 5 * 60_000;

/**
 * Loads data and, if asked, reloads it on a timer while the page is visible. Keeps showing
 * the last good data during a reload so screens don't flash.
 *
 * `key`: for loads whose deps include a moving time window (the "to" of the last 10 minutes),
 * pass what the card is really about (devEui + port). Then a deps change that only moves the
 * window keeps the old picture up while the new one loads, and only a new key clears it.
 * Without a key, any deps change clears the data (it may be about something else now).
 *
 * On a phone with weak signal: a timer tick is skipped while the last load is still going (so
 * slow replies don't pile up), and after "can't reach the server" the timer backs off 2x per
 * failure, up to 5 minutes, until a load works again.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[], refreshMs?: number, opts: { key?: string } = {}) {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const busy = useRef(false);
  const failures = useRef(0);
  const failedAt = useRef(0);
  const shownKey = useRef<string | undefined | null>(null);
  const key = useRef(opts.key);
  key.current = opts.key;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);
  const reload = useCallback(() => {
    const mine = ++seq.current;
    busy.current = true;
    setLoading(true);
    run()
      .then((d) => {
        if (mine !== seq.current) return;
        failures.current = 0;
        setData(d);
        setError(undefined);
      })
      .catch((e: unknown) => {
        if (mine !== seq.current) return;
        if (e instanceof ApiError && e.code === "offline") {
          failures.current++;
          failedAt.current = Date.now();
        }
        setError(e);
      })
      .finally(() => {
        if (mine !== seq.current) return;
        busy.current = false;
        setLoading(false);
      });
  }, [run]);

  useEffect(() => {
    if (key.current === undefined || key.current !== shownKey.current) {
      setData(undefined);
      setError(undefined);
    }
    shownKey.current = key.current;
    reload();
  }, [reload]);

  useEffect(() => {
    if (!refreshMs) return;
    const t = setInterval(() => {
      if (document.visibilityState !== "visible" || busy.current) return;
      if (failures.current) {
        const wait = Math.min(refreshMs * 2 ** failures.current, Math.max(BACKOFF_MAX_MS, refreshMs));
        if (Date.now() - failedAt.current < wait) return;
      }
      reload();
    }, refreshMs);
    // Coming back to the app: try straight away, whatever the back-off says.
    const onShow = () => document.visibilityState === "visible" && !busy.current && reload();
    document.addEventListener("visibilitychange", onShow);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onShow);
    };
  }, [reload, refreshMs]);

  return { data, error, loading, reload };
}

/** Shown over readings saved on the phone, when Think-Control can't be reached. */
export function SavedCopyBanner({ at, now, onRetry }: { at: number; now: number; onRetry: () => void }) {
  const time = new Date(at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  const day = new Date(at).toDateString() === new Date(now).toDateString() ? "" : ` on ${new Date(at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`;
  return (
    <div className="banner warn small" role="status" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <span>
        No signal: showing readings from {time}
        {day} ({ago(at, now)}). They'll update when Think-Control can be reached.
      </span>
      <button className="btn ghost small" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/**
 * Keeps one broken card (bad data from a device, a bug) from blanking the whole screen.
 * "Try again" draws it afresh; a change of `resetKey` (e.g. the page) does the same.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, { failed: boolean; key?: string | undefined }> {
  state: { failed: boolean; key?: string | undefined } = { failed: false, key: this.props.resetKey };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  static getDerivedStateFromProps(props: { resetKey?: string }, state: { failed: boolean; key?: string | undefined }) {
    return props.resetKey !== state.key ? { failed: false, key: props.resetKey } : null;
  }
  componentDidCatch(error: unknown) {
    console.error(error);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="card" role="alert" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ flex: 1 }}>Something went wrong.</span>
        <button className="btn" onClick={() => this.setState({ failed: false })}>
          Try again
        </button>
      </div>
    );
  }
}

/**
 * A screen whose code loads the first time it's shown (esbuild puts it in its own chunk), for
 * staff and occasional screens, so a farmer's phone doesn't download them on every open. A
 * spinner shows while it loads. On a farm the first try often fails on a weak signal, so it
 * tries twice; if both fail the error goes to the nearest ErrorBoundary, and its "Try again"
 * loads afresh (React keeps a failed lazy() failed, so a new one is made).
 */
export function lazyScreen<P extends object>(load: () => Promise<ComponentType<P>>) {
  const retry = () => load().catch(() => new Promise<void>((ok) => setTimeout(ok, 2000)).then(load));
  const make = () =>
    lazy(() =>
      retry()
        .then((c) => ({ default: c }))
        .catch((e: unknown) => {
          Screen = make();
          throw e;
        }),
    );
  let Screen = make();
  return function LazyScreen(props: P) {
    return (
      <Suspense fallback={<Spinner />}>
        <Screen {...props} />
      </Suspense>
    );
  };
}

/**
 * A small "i" beside a label that explains it: shows on hover or keyboard focus, and on a
 * tap on phones (no hover there). Tapping anywhere else closes it.
 */
export function HelpTip({ text, label = "What's this?" }: { text: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  return (
    <span className={`help-tip${open ? " open" : ""}`} ref={ref}>
      <button type="button" aria-label={label} aria-expanded={open} onClick={(e) => (e.preventDefault(), setOpen(!open))}>
        i
      </button>
      <span role="tooltip">{text}</span>
    </span>
  );
}

export function Section({ title, right, children }: { title: ReactNode; right?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="section-head">
        <h2>{title}</h2>
        {right && <div className="right">{right}</div>}
      </div>
      {children}
    </section>
  );
}
